import React from 'react';
import { fetchAgentScript, improveAgentScript, parseDefinition, updateAgentScriptDefinition } from './agentScriptsApi';

const FALLBACK_ERROR = 'Improvement failed, please try again.';
const INVALID_PLAN_ERROR = "Couldn't produce a valid improvement, try rephrasing your changes.";

/**
 * Owns the improve lifecycle plus a client-side snapshot of the script's prior
 * plan so the user can revert (the backend has no undo).
 *
 * - `improve` snapshots the current definition, then overwrites it in place.
 * - `revert` restores the snapshot via PATCH and clears it.
 * - `notFound` signals the caller to refresh its script list (404).
 */
export const useImproveAgentScript = () => {
  const [isImproving, setIsImproving] = React.useState(false);
  const [isReverting, setIsReverting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [descriptionError, setDescriptionError] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);
  const [snapshot, setSnapshot] = React.useState(null);

  const clearMessages = React.useCallback(() => {
    setError(null);
    setDescriptionError(null);
    setNotFound(false);
  }, []);

  const improve = React.useCallback(
    async ({ id, scriptName, description, currentDefinition }) => {
      clearMessages();

      const trimmedDescription = description.trim();
      if (!trimmedDescription) {
        setDescriptionError('Describe the changes you want.');
        return null;
      }

      setIsImproving(true);

      // Snapshot the prior plan before it is overwritten. Prefer the definition
      // already in hand; otherwise fetch the authoritative current record.
      let previousDefinition = currentDefinition ?? null;
      if (previousDefinition == null) {
        const record = await fetchAgentScript(id);
        previousDefinition = record ? record.definition_json : null;
      }

      const { status, data } = await improveAgentScript(id, trimmedDescription);
      setIsImproving(false);

      if (status === 200) {
        if (previousDefinition != null) {
          setSnapshot({ id, scriptName, definition: parseDefinition(previousDefinition) });
        }
        return data;
      }

      if (status === 404) {
        setNotFound(true);
        setError('That script no longer exists. Refreshing your list.');
      } else if (status === 400) {
        setError(data?.error || INVALID_PLAN_ERROR);
      } else {
        setError(data?.error || FALLBACK_ERROR);
      }
      return null;
    },
    [clearMessages]
  );

  const revert = React.useCallback(async () => {
    if (!snapshot) return false;
    setIsReverting(true);
    const ok = await updateAgentScriptDefinition(snapshot.id, snapshot.scriptName, snapshot.definition);
    setIsReverting(false);
    if (ok) {
      setSnapshot(null);
    } else {
      setError('Could not revert, please try again.');
    }
    return ok;
  }, [snapshot]);

  return {
    improve,
    revert,
    isImproving,
    isReverting,
    error,
    descriptionError,
    notFound,
    canRevert: Boolean(snapshot),
  };
};
